/**
 * responsive.ts — the single, app-wide responsiveness module.
 *
 * Every screen imports from here so behavior is consistent across mobile,
 * tablet, and desktop. Two ways to consume it:
 *
 *   1) useResponsive() hook — reactive; re-renders on resize/rotate. Preferred
 *      inside components. Exposes width/height, device class booleans, the
 *      scale helpers, and a `select({ mobile, tablet, desktop })` chooser.
 *
 *   2) scale / verticalScale / moderateScale — pure helpers for one-off sizing
 *      outside React (based on the current Dimensions snapshot).
 *
 * Breakpoints follow common device widths; the design's content column maxes
 * at 1240px (see MAX_CONTENT_WIDTH).
 */
import { Dimensions, PixelRatio, ScaledSize, useWindowDimensions } from 'react-native';

export const Breakpoints = {
  mobile: 0, // < 600
  tablet: 600, // 600–1023
  desktop: 1024, // >= 1024
} as const;

export const MAX_CONTENT_WIDTH = 1240;

// Reference guideline width (iPhone-ish) used by the scale helpers.
const GUIDELINE_BASE_WIDTH = 390;
const GUIDELINE_BASE_HEIGHT = 844;

export type DeviceClass = 'mobile' | 'tablet' | 'desktop';

export function getDeviceClass(width: number): DeviceClass {
  if (width >= Breakpoints.desktop) return 'desktop';
  if (width >= Breakpoints.tablet) return 'tablet';
  return 'mobile';
}

/** Linearly scale a size against device width (capped so desktop doesn't bloat). */
export function scale(size: number, width = Dimensions.get('window').width): number {
  const effective = Math.min(width, 600); // don't keep scaling up on huge screens
  return roundPx((effective / GUIDELINE_BASE_WIDTH) * size);
}

export function verticalScale(size: number, height = Dimensions.get('window').height): number {
  const effective = Math.min(height, 1000);
  return roundPx((effective / GUIDELINE_BASE_HEIGHT) * size);
}

/** Scale, but only move `factor` of the way (default 0.5) — for fonts/padding. */
export function moderateScale(size: number, factor = 0.5, width?: number): number {
  return roundPx(size + (scale(size, width) - size) * factor);
}

function roundPx(n: number): number {
  return Math.round(PixelRatio.roundToNearestPixel(n));
}

export interface ResponsiveInfo {
  width: number;
  height: number;
  deviceClass: DeviceClass;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  /** tablet OR desktop — i.e. the "wide" two-pane layouts. */
  isWide: boolean;
  isLandscape: boolean;
  scale: (size: number) => number;
  verticalScale: (size: number) => number;
  moderateScale: (size: number, factor?: number) => number;
  /** Pick a value by device class, falling back mobile → tablet → desktop. */
  select: <T>(opts: { mobile?: T; tablet?: T; desktop?: T; default?: T }) => T | undefined;
}

function buildInfo(win: ScaledSize): ResponsiveInfo {
  const { width, height } = win;
  const deviceClass = getDeviceClass(width);
  const isMobile = deviceClass === 'mobile';
  const isTablet = deviceClass === 'tablet';
  const isDesktop = deviceClass === 'desktop';
  return {
    width,
    height,
    deviceClass,
    isMobile,
    isTablet,
    isDesktop,
    isWide: isTablet || isDesktop,
    isLandscape: width > height,
    scale: (s) => scale(s, width),
    verticalScale: (s) => verticalScale(s, height),
    moderateScale: (s, factor = 0.5) => moderateScale(s, factor, width),
    select: (opts) => {
      if (isDesktop) return opts.desktop ?? opts.tablet ?? opts.mobile ?? opts.default;
      if (isTablet) return opts.tablet ?? opts.mobile ?? opts.default;
      return opts.mobile ?? opts.default;
    },
  };
}

/** Reactive responsiveness — re-renders on resize/rotate. */
export function useResponsive(): ResponsiveInfo {
  const win = useWindowDimensions();
  return buildInfo(win);
}

/** Non-reactive snapshot for use outside React. */
export function getResponsive(): ResponsiveInfo {
  return buildInfo(Dimensions.get('window'));
}
